import {
	Dispatch,
	SetStateAction,
	createContext,
	Fragment,
	ReactNode,
	useState,
} from "react"

// Router
import { NavLink as RouterNavLink } from "@/lib/router"

// Assets
import logo from "@/assets/icons/brand/logo.svg"

// UI
import { cn } from "@/lib/utils"
import { Dialog, DialogBackdrop, Transition, TransitionChild } from "@/lib/ui"
import { WelcomeText } from "@/components/WelcomeText"
import { WebappInstallButton } from "@/components/banners/WebappInstallButton"

// Navigation
import { UserMenu, Logo } from "@/components/navigation/UserMenu"
import { MainNavigation } from "@/components/navigation/MainNavigation"
import { Footer } from "@/components/navigation/Footer"

// Animations
import { motion } from "@/lib/animations"

// Icons
import { FiChevronLeft, FiChevronsRight } from "@/lib/icons"
import { MenuIcon } from "@/components/MenuIcon"

// Translations
import { useTrans } from "@/i18n"

// Analytics
import { sendEvent } from "@/lib/analytics"

// Feature flags
import { useFeatureFlags } from "@/context/user"

// Types
interface AuthLayoutProps {
	children?: ReactNode
}

export const AuthLayoutContext = createContext<{
	isMobileMenuOpen: boolean
	isDesktopMenuOpen: boolean
	setMobileMenuOpen: Dispatch<SetStateAction<boolean>>
	setIsDesktopMenuClosed: Dispatch<SetStateAction<boolean>>
}>(null!)

/**
 * AuthLayout
 *
 * @param param0
 * @returns
 */
export function AuthLayout({ children }: AuthLayoutProps) {
	// State
	const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [isDesktopMenuOpen, setIsDesktopMenuClosed] = useState(true)

	// Translate
	const t = useTrans()

	// Context
	const { getFeatureFlagValue } = useFeatureFlags()

	function setMobileMenuOpenProxy(context = "mobilemenuiconontoggle") {
		const nextValue = !isMobileMenuOpen
		sendEvent("layout", context, {
			label: nextValue ? "open" : "close",
		})
		setMobileMenuOpen(nextValue)
	}

	function setIsDesktopMenuClosedProxy() {
		const nextValue = !isDesktopMenuOpen
		sendEvent("layout", "desktopmenuicon", {
			label: nextValue ? "open" : "close",
		})
		setIsDesktopMenuClosed(nextValue)
	}

	return (
		<AuthLayoutContext.Provider
			value={{
				isMobileMenuOpen,
				isDesktopMenuOpen,
				setMobileMenuOpen,
				setIsDesktopMenuClosed,
			}}
		>
			<div className="h-full min-h-full bg-gray-50 md:flex md:h-auto">
				{/* sidebar: desktop menu, a.k.a. the left nav on md: viewports and above */}
				<motion.div
					className="hidden min-h-full shrink-0 bg-white text-black md:block"
					initial={{ width: 256 }}
					animate={{ width: isDesktopMenuOpen ? 256 : 40 }}
					transition={{
						duration: 0.4,
						ease: [0.22, 0.65, 0.275, 1],
					}}
				>
					<div className="sticky top-0 flex h-screen w-[256px] flex-col overflow-y-auto">
						<div
							className={cn(
								"flex h-14 shrink-0 items-center bg-primary-500 shadow-[0px_1px_3px_rgba(0,0,0,0.25)] 2xl:h-16",
								"pl-2",
							)}
						>
							{isDesktopMenuOpen === true && <Logo />}

							{/* menu icon for collapsible nav on desktop */}
							{getFeatureFlagValue(
								"ENABLE_MENU_COLLAPSE_ON_DESKTOP",
							) === true && (
								<button
									onClick={setIsDesktopMenuClosedProxy}
									className="hidden h-full items-center justify-center whitespace-nowrap hover:bg-gray-50 md:flex"
								>
									{isDesktopMenuOpen === true ? (
										<FiChevronLeft
											aria-hidden="true"
											className="size-4 text-gray-500"
										/>
									) : (
										<FiChevronsRight
											aria-hidden="true"
											className="size-4 text-gray-500"
										/>
									)}
								</button>
							)}
						</div>

						{/* Main nav */}
						<MainNavigation />

						{/* Sticky footer for UserMenu */}
						<div className="mt-auto border-t border-gray-200 py-4">
							<UserMenu />
						</div>
					</div>
				</motion.div>

				{/* mobile nav */}
				<Transition show={isMobileMenuOpen} as={Fragment}>
					<Dialog
						as="div"
						className="md:hidden"
						onClose={() =>
							setMobileMenuOpenProxy("mobilemenuclose")
						}
					>
						<div className="fixed inset-0 z-40 flex h-full">
							<TransitionChild as={Fragment}>
								<DialogBackdrop
									className={cn(
										"fixed inset-0 bg-gray-600 bg-opacity-75",
										"transition duration-300 ease-in-out data-[closed]:opacity-0",
									)}
								/>
							</TransitionChild>
							<TransitionChild as={Fragment}>
								<div
									className={cn(
										"relative flex w-full max-w-[256px] flex-1 flex-col bg-white",
										"transition duration-300 ease-in-out data-[closed]:-translate-x-full sm:max-w-sm",
									)}
								>
									<TransitionChild as={Fragment}>
										<div
											className={cn(
												"absolute right-0 top-1 -mr-14 p-1 transition duration-300 ease-in-out data-[closed]:opacity-0",
												"data-[closed]:-translate-x-full",
											)}
										>
											<button
												type="button"
												className="flex size-12 items-center justify-center"
												onClick={() =>
													setMobileMenuOpenProxy(
														"mobilemenuiconclose",
													)
												}
											>
												<MenuIcon
													className="size-6"
													aria-hidden="true"
													isOpen={false}
												/>
												<span className="sr-only">
													{t(
														"common.navigation.close_sidebar.sr_text",
													)}
												</span>
											</button>
										</div>
									</TransitionChild>
									<div className="flex h-screen flex-1 flex-col overflow-y-auto">
										<div
											className={cn(
												"flex h-14 shrink-0 items-center bg-primary-500 shadow-[0px_1px_3px_rgba(0,0,0,0.25)] 2xl:h-16",
												"pl-2",
											)}
										>
											{isDesktopMenuOpen === true && (
												<Logo />
											)}

											{/* menu icon for collapsible nav on desktop */}
											{getFeatureFlagValue(
												"ENABLE_MENU_COLLAPSE_ON_DESKTOP",
											) === true && (
												<button
													onClick={
														setIsDesktopMenuClosedProxy
													}
													className="hidden h-full items-center justify-center whitespace-nowrap hover:bg-gray-50 md:flex"
												>
													{isDesktopMenuOpen ===
													true ? (
														<FiChevronLeft
															aria-hidden="true"
															className="size-4 text-gray-500"
														/>
													) : (
														<FiChevronsRight
															aria-hidden="true"
															className="size-4 text-gray-500"
														/>
													)}
												</button>
											)}
										</div>
										<div className="flex-1">
											<MainNavigation />
										</div>
										<div className="shrink-0 space-y-3 border-t border-gray-200 py-4">
											<UserMenu />
										</div>
									</div>
								</div>
							</TransitionChild>
							<div className="w-14 shrink-0" aria-hidden="true">
								{/* Dummy element to force sidebar to shrink to fit close icon */}
							</div>
						</div>
					</Dialog>
				</Transition>

				<div className="flex min-h-full w-full grow flex-col md:max-w-[calc(100%_-_256px)] md:pt-0">
					<div className="sticky top-0 z-30 grid h-14 w-full shrink-0 grid-cols-[auto_auto] justify-between gap-3 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.125)] md:static md:grid-cols-[1fr_auto] md:px-4 2xl:h-16">
						{/* Welcome text component */}
						<WelcomeText />

						{/* Logo */}
						<div className="flex items-center md:hidden">
							<RouterNavLink to="/" className="px-4">
								<img
									src={logo}
									alt={t("common.brand.name")}
									className="h-auto w-8 md:w-6"
								/>
							</RouterNavLink>
						</div>

						<div className="order-2 flex h-full items-center justify-center md:order-1 md:hidden">
							<WebappInstallButton
								className={"block md:hidden"}
							/>

							{/* mobile nav menu icon */}
							<button
								type="button"
								className="w-14 px-3"
								onClick={() => setMobileMenuOpenProxy()}
								data-testid="navigation.open_sidebar"
							>
								<span className="sr-only">
									{t(
										"common.navigation.open_sidebar.sr_text",
									)}
								</span>
								<MenuIcon
									isOpen={true}
									aria-hidden="true"
									className="h-3 w-6"
								/>
							</button>
						</div>
					</div>

					{/** Content */}
					<div className="flex size-full grow flex-col">
						{children}
					</div>

					{/** Footer */}
					<div className="shrink">
						<Footer />
					</div>
				</div>
			</div>
		</AuthLayoutContext.Provider>
	)
}
